// NOTE: In development, React StrictMode may cause the submit button to appear non-functional
// due to double-invocation of blur event handlers in useKeyboardDetection. This is a development-only
// quirk and does not affect production builds where StrictMode is disabled.
import c from 'classnames';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { LoadingLines } from '@repo/ui';

import { useAnalytics } from '../../contexts/analytics/AnalyticsProvider';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { useTinyAIChatTRPC } from '../../hooks/useTinyAIChatTRPC';
import { useTinyAIDisplayLocation } from '../../hooks/useTinyAIDisplayLocation';
import { useTinyAIPageContext } from '../../hooks/useTinyAIPageContext';
import useImpersonate from '../../services/auth/useImpersonate';
import { useChatbotPrompts } from '../../services/chatbotPrompts/useChatbotPrompts';
import { getTinyAccountById } from '../../store/account';
import { TinyAIComposerInput } from './components/TinyAIComposerInput';
import { TinyAIGreeting } from './components/TinyAIGreeting';
import { TinyAIHeader } from './components/TinyAIHeader';
import { TinyAIMessageList } from './components/TinyAIMessageList';
import { useBodyScrollLock } from './hooks/useBodyScrollLock';
import { useKeyboardDetection } from './hooks/useKeyboardDetection';
import { useManualTouchScroll } from './hooks/useManualTouchScroll';
import { useSwipeToClose } from './hooks/useSwipeToClose';
import { useTinyAIChatComposer } from './hooks/useTinyAIChatComposer';
import { useTinyAIPendingPromptDispatcher } from './hooks/useTinyAIPendingPromptDispatcher';
import { useTinyAIPromptShownAnalytics } from './hooks/useTinyAIPromptShownAnalytics';
import { useTinyAIScrollOrchestrator } from './hooks/useTinyAIScrollOrchestrator';
import { isIOS } from './utils/platformDetection';

import styles from './TinyAIChatWindow.module.css';

interface TinyAIChatWindowProps {
  onKeyboardVisibilityChange?: (isVisible: boolean) => void;
  onClose?: () => void;
  scrollContainerRef?: RefObject<HTMLElement>;
}

export const TinyAIChatWindow = ({
  onKeyboardVisibilityChange,
  onClose,
  scrollContainerRef,
}: TinyAIChatWindowProps) => {
  // Local UI state
  const [isExpanded, setIsExpanded] = useState(false);

  // Impersonation view-only gate: ops admin viewing another user can see
  // history but cannot send new messages or trigger prompt chips.
  const { impersonateId } = useImpersonate();
  const isViewOnly = !!impersonateId;

  // Account, page context, and prompt data
  const pageContext = useTinyAIPageContext();
  const tinyAccount = useSelector(
    getTinyAccountById(pageContext.tinyAccountId ?? null),
  );
  const { trackEvent } = useAnalytics();
  const displayLocation = useTinyAIDisplayLocation();
  const { data: prompts } = useChatbotPrompts(displayLocation);

  // Chat session bundle (chat hook return + derived isAnswering)
  const chatTRPC = useTinyAIChatTRPC({
    pageContext,
    tinyAccountId: tinyAccount?.id,
    kitId: pageContext.kitId,
  });
  const chat = {
    ...chatTRPC,
    isAnswering: chatTRPC.isLoading || chatTRPC.streamingMessageId !== null,
  };

  // Scroll behavior
  const scroll = useTinyAIScrollOrchestrator({
    messages: chat.messages,
    isLoadingHistory: chat.isLoadingHistory,
    isLoadingMoreHistory: chat.isLoadingMoreHistory,
    hasMoreHistory: chat.hasMoreHistory,
    loadMoreHistory: chat.loadMoreHistory,
  });

  // Mobile chrome (viewport, body lock)
  const { isMobile } = useMobileDetection();
  useBodyScrollLock({
    enabled: isMobile,
    containerRef: scrollContainerRef,
  });

  // Composer (textarea state + send handlers)
  const {
    inputValue,
    setInputValue,
    inputRef,
    handleSend,
    handleKeyDown,
    canSend,
  } = useTinyAIChatComposer({
    sendMessage: chat.sendMessage,
    isAnswering: chat.isAnswering,
    isMobile,
    onSendStart: scroll.resetScrollLockOnSend,
  });

  // Keyboard detection and swipe-to-close gesture
  const { isKeyboardVisible, viewportHeight, offsetTop } = useKeyboardDetection(
    {
      enabled: isMobile,
      inputRef,
      onVisibilityChange: onKeyboardVisibilityChange,
    },
  );
  const { onTouchStart, onTouchMove, onTouchEnd } = useSwipeToClose({
    onClose,
  });
  useManualTouchScroll({
    enabled: isMobile && isKeyboardVisible && isIOS(),
    containerRef: scroll.messagesContainerRef,
  });

  // Side effects: pending prompt dispatch and analytics
  useTinyAIPendingPromptDispatcher({
    sessionId: chat.sessionId,
    isLoadingHistory: chat.isLoadingHistory,
    sendMessage: chat.sendMessage,
  });
  useTinyAIPromptShownAnalytics({
    prompts,
    hasMessages: chat.messages.length > 0,
  });

  const prevStreamingMessageIdRef = useRef<string | null>(null);
  useEffect(() => {
    const justFinished =
      prevStreamingMessageIdRef.current !== null &&
      chat.streamingMessageId === null;
    prevStreamingMessageIdRef.current = chat.streamingMessageId;
    if (justFinished) {
      trackEvent('TinyAI responded', {
        page_context: pageContext,
      });
    }
  }, [chat.streamingMessageId, trackEvent, pageContext]);

  // Prompt chip handler
  const handlePromptClick = async (promptText: string, promptId: number) => {
    trackEvent('TinyAI in-widget prompt clicked', {
      prompt_text: promptText,
      display_location: displayLocation!,
      page_context: pageContext,
    });
    trackEvent('TinyAI message sent', {
      page_context: pageContext,
      source: 'chat',
      prompt_text: promptText,
      display_location: displayLocation || undefined,
    });
    await chat.sendMessage(promptText, true, promptId, pageContext);
  };

  return (
    <div
      className={c(styles.chatWindow, {
        [styles.expanded]: isExpanded,
        [styles.keyboardOpen]: isMobile && isKeyboardVisible,
      })}
      style={
        isMobile && isKeyboardVisible && viewportHeight
          ? {
              height: `${viewportHeight - 32}px`,
              // Safety net: if iOS still shifts the visual viewport (offsetTop > 0),
              // pin the window to the shifted origin, preserving the CSS top margin.
              ...(offsetTop > 0
                ? {
                    top: `calc(${offsetTop}px + max(1rem, env(safe-area-inset-top)))`,
                  }
                : {}),
            }
          : undefined
      }
    >
      <div
        className={styles.swipeTouchArea}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className={styles.swipeIndicator} />
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.topSection}>
          <TinyAIHeader
            isExpanded={isExpanded}
            onToggleExpand={() => setIsExpanded(!isExpanded)}
          />

          {chat.messages.length === 0 && !chat.isLoadingHistory && (
            <TinyAIGreeting
              firstName={tinyAccount?.first_name}
              prompts={isViewOnly ? undefined : prompts}
              onPromptClick={handlePromptClick}
            />
          )}

          {chat.isLoadingHistory && (
            <div className={styles.historyLoading}>
              <LoadingLines lines={10} />
            </div>
          )}
        </div>

        <TinyAIMessageList chat={chat} scroll={scroll} />

        {!isViewOnly && (
          <TinyAIComposerInput
            inputRef={inputRef}
            value={inputValue}
            onChange={setInputValue}
            onKeyDown={handleKeyDown}
            onSend={handleSend}
            canSend={canSend}
          />
        )}
      </div>
    </div>
  );
};
